<template>
  <div class="@container" ref="container">
    <p class="text-error text-body-small mb-2">{{ error }}</p>
    <div
      class="rounded-large bg-surface-container-low focus-within:border-primary relative flex w-full flex-col border-3 border-transparent"
    >
      <div class="overflow-hidden" :class="[expanded ? 'h-12' : 'h-0']">
        <div class="flex gap-1 p-2 pb-0">
          <BaseIconButton
            icon="format_bold"
            color="outlined"
            size="XS"
            :selected="editor?.isActive('bold')"
            @click="toggleBold"
          />
          <BaseIconButton
            icon="format_underlined"
            color="outlined"
            size="XS"
            :selected="editor?.isActive('underline')"
            @click="toggleUnderline"
          />
          <BaseIconButton
            icon="format_list_bulleted"
            color="outlined"
            size="XS"
            :selected="editor?.isActive('bulletList')"
            @click="toggleBulletList"
          />
          <BaseMenu
            v-if="!hideHeadlines"
            class="ml-auto"
            :items="
              fontList.map((option) => ({
                id: option.level.toString(),
                title: option.label,
                callback: () => toggleHeading(option.level as Level),
              }))
            "
            v-slot="{ open }"
          >
            <BaseTextButton size="XS" color="outlined" shape="square" :force-pressed="open">{{
              selectedFont
            }}</BaseTextButton>
          </BaseMenu>
        </div>
      </div>

      <EditorContent
        :editor="editor"
        class="flex"
        :class="[
          expanded && !compact && 'min-h-40',
          !expanded && !compact && 'min-h-20',
          expanded && compact && 'min-h-16',
          !expanded && compact && 'min-h-10',
        ]"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, onMounted, useTemplateRef } from 'vue'
  import { EditorContent, type JSONContent } from '@tiptap/vue-3'
  import { type Level } from '@tiptap/extension-heading'
  import { useWysiwygEditor } from '@/core/composables/useWysiwygEditor'
  import BaseMenu from '../menu/BaseMenu.vue'
  import BaseIconButton from '../button/icon-button/BaseIconButton.vue'
  import BaseTextButton from '../button/text-button/BaseTextButton.vue'
  import { useFocusWithin } from '@vueuse/core'

  interface Props {
    autofocus?: boolean
    error?: string
    placeholder?: string
    isAlwaysExpanded?: boolean
    compact?: boolean
    hideHeadlines?: boolean
  }

  const value = defineModel<JSONContent | undefined>({ required: true })

  const props = defineProps<Props>()
  const emit = defineEmits<{
    (e: 'update:model-value', value: JSONContent | undefined): void
  }>()

  const {
    editor,
    toggleBold,
    toggleBulletList,
    toggleHeading,
    toggleUnderline,
    selectedFont,
    fontList,
  } = useWysiwygEditor(value.value, false, props.placeholder, (value: JSONContent | undefined) =>
    emit('update:model-value', value),
  )

  onMounted(() => {
    if (props.autofocus && editor.value) {
      editor.value?.commands.focus('end')
    }
  })

  const container = useTemplateRef<HTMLDivElement>('container')

  const { focused } = useFocusWithin(container)

  const expanded = computed(() => {
    return focused.value || props.isAlwaysExpanded || (editor.value?.getText()?.length ?? 0) > 0
  })
</script>
